import React, { useRef, useEffect, useState, FormEvent } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // supports lists, tables, etc.

const API_BASE = "https://ai-doctor-api-750084366058.us-west1.run.app";

interface Message {
  sender: string;
  text: string;
}

interface DisclaimerModalProps {
  showDisclaimer: boolean;
  onClose: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  safeArea: {
    background: '#f7f7f7',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '400px',
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '80vh',
    padding: '24px',
    position: 'relative',
  },
  header: {
    fontSize: '22px',
    fontWeight: 'bold',
    marginBottom: '16px',
    textAlign: 'center',
  },
  chat: {
    flexGrow: 1,
    overflowY: 'auto',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
  },
  message: {
    padding: '10px',
    borderRadius: '12px',
    margin: '6px 0',
    maxWidth: '75%',
    wordBreak: 'break-word',
  },
  user: {
    background: '#cce5ff',
    alignSelf: 'flex-end',
  },
  bot: {
    background: '#e2e3e5',
    alignSelf: 'flex-start',
  },
  sender: {
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  messageText: {
    fontSize: '16px',
    lineHeight: '22px',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    borderTop: '1px solid #ccc',
    marginTop: '4px',
    paddingTop: '10px',
  },
  input: {
    flex: 1,
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    marginRight: '10px',
    background: '#fff',
    fontSize: '16px',
  },
  button: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: '#007bff',
    color: '#fff',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  disclaimerOverlay: {
    position: 'fixed',
    left: 0,
    top: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  disclaimerBox: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
};

function DisclaimerModal({ showDisclaimer, onClose }: DisclaimerModalProps) {
  if (!showDisclaimer) return null;
  return (
    <div style={styles.disclaimerOverlay}>
      <div style={styles.disclaimerBox}>
        <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>🛑 Medical Disclaimer</div>
        <div style={{ fontSize: '16px', marginBottom: '10px' }}>
          This app is an AI-powered virtual health assistant designed for informational purposes only.
          It is NOT a substitute for professional medical advice, diagnosis, or treatment.<br /><br />
          Always seek the advice of a qualified healthcare provider with any questions you may have
          regarding a medical condition.<br /><br />
          If you are experiencing a medical emergency, call 911 or emergency services immediately.
        </div>
        <button style={styles.button} onClick={onClose}>I Understand</button>
      </div>
    </div>
  );
}

const ChatApp: React.FC = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function startSession() {
      try {
        const response = await axios.post(`${API_BASE}/api/start`);
        setSessionId(response.data.session_id);
        setMessages([{ sender: "Assistant", text: "Hello, how can I assist you with your symptoms today?" }]);
        setTimeout(() => inputRef.current?.focus(), 100);
      } catch (error) {
        console.error("Error starting session:", error);
      }
    }
    startSession();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !sessionId) return;

    const userMessage: Message = { sender: "You", text: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/api/chat`, {
        session_id: sessionId,
        message: userMessage.text,
      });
      const botReply: Message = { sender: "Assistant", text: response.data.response };
      setMessages((prev) => [...prev, botReply]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { sender: "Assistant", text: "Sorry, something went wrong. Please try again later." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.safeArea}>
      <DisclaimerModal showDisclaimer={showDisclaimer} onClose={() => setShowDisclaimer(false)} />
      <div style={styles.container}>
        <div style={styles.header}>AI Health Assistant</div>
        <div style={styles.chat}>
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                ...styles.message,
                ...(msg.sender === "You" ? styles.user : styles.bot),
              }}
            >
              <div style={{ ...styles.sender, fontWeight: 'bold' }}>
                {msg.sender === "You" ? "You:" : "Assistant:"}
              </div>
              <div style={styles.messageText}>
                {msg.sender === "Assistant" ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      ul: ({ node, ...props }) => <ul style={{ marginLeft: 18 }} {...props} />,
                      li: ({ node, ...props }) => <li style={{ marginBottom: 4 }} {...props} />,
                      strong: ({ node, ...props }) => (
                        <strong style={{ color: '#b85a00' }} {...props} />
                      ),
                      p: ({ node, ...props }) => <p style={{ margin: '6px 0' }} {...props} />,
                    }}
                  >
                    {msg.text.replace(/^Assistant:\s*/i, '')}
                  </ReactMarkdown>
                ) : (
                  <span>{msg.text}</span>
                )}
              </div>
            </div>
          ))}
          {loading && <div>Loading...</div>}
          <div ref={chatEndRef} />
        </div>

        <form style={styles.inputContainer} onSubmit={sendMessage}>
          <input
            ref={inputRef}
            style={styles.input}
            placeholder="Describe your symptoms..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button type="submit" style={styles.button}>Send</button>
        </form>
      </div>
    </div>
  );
};

export default ChatApp;
